import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { useCompBooking } from './payment-review.api';
import type { PaymentReviewItem } from './types';

/**
 * Confirm a booking was given away on purpose.
 *
 * The reason is the point of the dialog: this is the one outcome that closes an
 * item without money appearing, so the next person needs to be able to see why.
 */
export function CompDialog({
  item,
  open,
  onOpenChange,
}: {
  item: PaymentReviewItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const comp = useCompBooking();

  const [reason, setReason] = useState('');
  const [hydrated, setHydrated] = useState<string | null>(null);

  // Clear once per item; the dialog stays mounted between openings.
  if (item && hydrated !== item.bookingId) {
    setHydrated(item.bookingId);
    setReason('');
  }

  if (!item) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('paymentReview.confirmComped')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('paymentReview.confirmCompedHelp', {
              name: item.userName ?? '',
              event: item.eventName ?? '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Textarea
          rows={3}
          maxLength={500}
          value={reason}
          placeholder={t('paymentReview.compedReasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
        />

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={comp.isPending}
            onClick={() =>
              comp.mutate(
                { bookingId: item.bookingId, reason },
                {
                  onSuccess: () => {
                    toast.success(t('paymentReview.comped'));
                    onOpenChange(false);
                  },
                  onError: () => toast.error(t('paymentReview.actionFailed')),
                }
              )
            }
          >
            {comp.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('paymentReview.confirmComped')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
